import apiService from "@/core/services/apiService";

export type State = { classes: object };
import {Module, VuexModule, Mutation, Action} from "vuex-module-decorators";
import {Mutations} from "@/store/enums/store.enums";
import {TetturaActions, TetturaMutation} from "@/modules/tettura/store/enums";

@Module
export default class Store extends VuexModule {

    listingsParams = { page: 1, results: 10, search: null, sort: [], filter: {} };
    listingsCache = [];
    listingsTotal = 0;

    architectsParams = { page: 1, results: 10, search: null, sort: [], filter: {} };
    architectsCache = [];
    architectsTotal = 0;

    reviewsParams = { page: 1, results: 10, search: null, sort: [], filter: {} };
    reviewsCache = [];
    reviewsTotal = 0;

    ordersParams = { page: 1, results: 10, search: null, sort: [], filter: {} };
    ordersCache = [];
    ordersTotal = 0;

    payoutsCache = [];

    accountsParams = { page: 1, results: 25, search: null, filter: {} };
    accountsCache = [];
    accountsTotal = 0;

    get getTetturaListingsResult() {
        return this.listingsCache;
    }

    get getTetturaListingsTotal() {
        return this.listingsTotal;
    }

    get getTetturaArchitectsResult() {
        return this.architectsCache;
    }

    get getTetturaArchitectsTotal() {
        return this.architectsTotal;
    }

    get getTetturaReviewsResult() {
        return this.reviewsCache;
    }

    get getTetturaReviewsTotal() {
        return this.reviewsTotal;
    }

    get getTetturaOrdersResult() {
        return this.ordersCache;
    }

    get getTetturaOrdersTotal() {
        return this.ordersTotal;
    }

    get getTetturaPayoutsResult() {
        return this.payoutsCache;
    }

    get getTetturaAccountsResult() {
        return this.accountsCache;
    }

    get getTetturaAccountsTotal() {
        return this.accountsTotal;
    }

    @Mutation
    [TetturaMutation.SET_LISTINGS_RESULT]({listParams, listTotal, listCache}) {
        this.listingsParams = listParams;
        this.listingsCache = listCache;
        this.listingsTotal = listTotal;
    }

    @Mutation
    [TetturaMutation.SET_ARCHITECTS_RESULT]({listParams, listTotal, listCache}) {
        this.architectsParams = listParams;
        this.architectsCache = listCache;
        this.architectsTotal = listTotal;
    }

    @Mutation
    [TetturaMutation.SET_REVIEWS_RESULT]({listParams, listTotal, listCache}) {
        this.reviewsParams = listParams;
        this.reviewsCache = listCache;
        this.reviewsTotal = listTotal;
    }

    @Mutation
    [TetturaMutation.SET_ORDERS_RESULT]({listParams, listTotal, listCache}) {
        this.ordersParams = listParams;
        this.ordersCache = listCache;
        this.ordersTotal = listTotal;
    }

    @Mutation
    [TetturaMutation.SET_PAYOUTS_RESULT](items) {
        this.payoutsCache = items;
    }

    @Mutation
    [TetturaMutation.SET_ACCOUNTS_RESULT]({listParams, listTotal, listCache}) {
        this.accountsParams = listParams;
        this.accountsCache = listCache;
        this.accountsTotal = listTotal;
    }

    // ─── Listings ─────────────────────────────────────────────────────

    @Action
    [TetturaActions.REFRESH_LISTINGS]() {
        this.context.dispatch(TetturaActions.FETCH_LISTINGS, this.listingsParams);
    }

    @Action
    [TetturaActions.FETCH_LISTINGS](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("tettura/listings-list", params).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(TetturaMutation.SET_LISTINGS_RESULT, {
                    listParams: params,
                    listTotal: data.result.total,
                    listCache: data.result.items
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.GET_LISTING](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`tettura/listing-get/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.UPDATE_LISTING](payload) {

        return new Promise((resolve, reject) => {

            const {editId, data} = payload;

            apiService.post(`tettura/listing-update/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(TetturaActions.REFRESH_LISTINGS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.UPDATE_LISTING_TRANSLATION](payload) {

        return new Promise((resolve, reject) => {

            const {editId, data} = payload;

            apiService.post(`tettura/listing-translation/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.SET_LISTING_STATUS](payload) {

        return new Promise((resolve, reject) => {

            const {editId, status} = payload;

            apiService.put(`tettura/listing-status/${editId}`, {status: status} as any).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(TetturaActions.REFRESH_LISTINGS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.DELETE_LISTING](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`tettura/listing-delete/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(TetturaActions.REFRESH_LISTINGS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // ─── Architekten ──────────────────────────────────────────────────

    @Action
    [TetturaActions.REFRESH_ARCHITECTS]() {
        this.context.dispatch(TetturaActions.FETCH_ARCHITECTS, this.architectsParams);
    }

    @Action
    [TetturaActions.FETCH_ARCHITECTS](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("tettura/architects-list", params).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(TetturaMutation.SET_ARCHITECTS_RESULT, {
                    listParams: params,
                    listTotal: data.result.total,
                    listCache: data.result.items
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.GET_ARCHITECT](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`tettura/architect-get/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.UPDATE_ARCHITECT](payload) {

        return new Promise((resolve, reject) => {

            const {editId, data} = payload;

            apiService.post(`tettura/architect-update/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(TetturaActions.REFRESH_ARCHITECTS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.UPDATE_ARCHITECT_TRANSLATION](payload) {

        return new Promise((resolve, reject) => {

            const {editId, data} = payload;

            apiService.post(`tettura/architect-translation/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.SET_ARCHITECT_STATUS](payload) {

        return new Promise((resolve, reject) => {

            const {editId, status} = payload;

            apiService.put(`tettura/architect-status/${editId}`, {status: status} as any).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(TetturaActions.REFRESH_ARCHITECTS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // ─── Reviews (Moderation) ─────────────────────────────────────────

    @Action
    [TetturaActions.REFRESH_REVIEWS]() {
        this.context.dispatch(TetturaActions.FETCH_REVIEWS, this.reviewsParams);
    }

    @Action
    [TetturaActions.FETCH_REVIEWS](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("tettura/reviews-list", params).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(TetturaMutation.SET_REVIEWS_RESULT, {
                    listParams: params,
                    listTotal: data.result.total,
                    listCache: data.result.items
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.SET_REVIEW_STATUS](payload) {

        return new Promise((resolve, reject) => {

            const {editId, status} = payload;

            apiService.put(`tettura/review-status/${editId}`, {status: status} as any).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(TetturaActions.REFRESH_REVIEWS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // ─── Orders (read-only) ───────────────────────────────────────────

    @Action
    [TetturaActions.REFRESH_ORDERS]() {
        this.context.dispatch(TetturaActions.FETCH_ORDERS, this.ordersParams);
    }

    @Action
    [TetturaActions.FETCH_ORDERS](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("tettura/orders-list", params).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(TetturaMutation.SET_ORDERS_RESULT, {
                    listParams: params,
                    listTotal: data.result.total,
                    listCache: data.result.items
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [TetturaActions.SET_ORDER_STATUS]({editId, status}) {
        return new Promise<void>((resolve, reject) => {
            apiService.put(`tettura/order-status/${editId}`, {status: status} as any).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve();
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ─── Payouts ──────────────────────────────────────────────────────

    @Action
    [TetturaActions.FETCH_PAYOUTS]() {
        return new Promise<void>((resolve, reject) => {
            apiService.post("tettura/payouts-open", {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                this.context.commit(TetturaMutation.SET_PAYOUTS_RESULT, data.result.items);
                resolve();
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [TetturaActions.PAY_ARCHITECT](payload) {
        return new Promise<any>((resolve, reject) => {
            apiService.post("tettura/payout-pay", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [TetturaActions.EXPORT_SEPA]() {
        return new Promise<any>((resolve, reject) => {
            apiService.post("tettura/payout-sepa", {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // ─── Accounts (Konten-Sperre) ─────────────────────────────────────

    @Action
    [TetturaActions.FETCH_ACCOUNTS](params) {
        return new Promise<void>((resolve, reject) => {
            apiService.post("tettura/accounts-list", params).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                this.context.commit(TetturaMutation.SET_ACCOUNTS_RESULT, {
                    listParams: params,
                    listTotal: data.result.total,
                    listCache: data.result.items
                });
                resolve();
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [TetturaActions.SET_ACCOUNT_STATE]({editId, state}) {
        return new Promise<void>((resolve, reject) => {
            apiService.put(`tettura/account-state/${editId}`, {state: state} as any).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve();
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

}
